import { HttpClient, HttpParams, httpResource } from '@angular/common/http';
import { computed, effect, inject, Injectable, InjectionToken, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, first, map, Observable, switchMap } from 'rxjs';

import { environment } from '@environment';

import { Workspace } from './settings/workspace.model';
import { User } from './users/user.model';
import { Result } from '@common/types/http-types';
import { AuthService } from '@common/services/auth.service';
import { handleError } from '@common/utils/http-utils';
import { StorageService } from '@common/services/storage.service';

export const WORKSPACE_TOKEN = new InjectionToken<Workspace>('workspace');
export const USER_TOKEN = new InjectionToken<User>('user');

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private http = inject(HttpClient);
  private $auth = inject(AuthService);
  private router = inject(Router);
  private $storage = inject(StorageService);

  workspacesUrl = computed(() => {
    const user = this.user();
    const workspace = this.workspace();
    if (user && workspace) {
      return `${environment.baseUrl}/v1/user/workspaces`;
    }
    return undefined;
  });

  private workspacesResource = httpResource<Result<Workspace[]>>(() => {
    return this.workspacesUrl();
  });

  readonly workspace = signal<Workspace | undefined>(undefined, {
    equal: (a, b) => {
      return a?.id === b?.id;
    },
  });

  readonly user = signal<User | undefined>(undefined);

  workspaces = computed(() => {
    return this.workspacesResource.value()?.data ?? ([] as Workspace[]);
  });

  constructor() {
    effect(() => {
      if (this.workspace()) {
        this.$storage.set('workspace', `${this.workspace()?.id}`);
      }
    });
  }

  private restartNavigation(): void {
    const currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
    });
  }

  saveWorkspace(workspace: Workspace): Observable<Workspace> {
    const params = new HttpParams().set('workspaceId', workspace.id);
    return this.http.get<Result<Workspace>>(environment.baseUrl + '/v1/user/workspace', { params }).pipe(
      first(),
      catchError(handleError),
      map(resp => resp.data as Workspace)
    );
  }

  saveUser(user: User): Observable<Result<User>> {
    this.$auth.updateUser(user).catch(() => {
      // ignore
    });
    return this.http.put<Result<User>>(environment.baseUrl + '/v1/user', user).pipe(catchError(handleError));
  }

  deleteUser(): Observable<void> {
    return this.http.delete<void>(environment.baseUrl + '/v1/user').pipe(
      first(),
      switchMap(() => {
        return this.$auth.logout();
      }),
      catchError(handleError)
    );
  }
}
